import { Box, Card, Divider, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw, F0TagStatus } from '@factorialco/f0-react'
import { Link, LinkRemove } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { getPeopleCount } from 'modules/project_management/lib/constants'
import { convertCentsToPercentage } from 'modules/project_management/lib/profitabilityCalculations'

interface IRateInfoCard {
  startsOn: string
  endsOn: string
  currency: string
  chargingCostCents: number
  billingRateCents: number
  markupPercentageCents: number
  marginPercentageCents: number
  totalCount?: number
  rateType?: 'custom' | 'job'
  areJobLevelRates?: boolean
}

const RateInfoCard = ({
  startsOn,
  endsOn,
  currency,
  chargingCostCents,
  billingRateCents,
  markupPercentageCents,
  marginPercentageCents,
  totalCount = 0,
  rateType,
  areJobLevelRates,
}: IRateInfoCard) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const costRateLabel = areJobLevelRates
    ? i18n.t('project_management.jobs_and_rates.sidepanels.form.max_cost_rate.label')
    : i18n.t('project_management.people_and_rates.form.cost_rate.label')

  const markupLabel = areJobLevelRates
    ? i18n.t('project_management.jobs_and_rates.sidepanels.form.min_markup.label')
    : i18n.t('project_management.people_and_rates.form.markup.label')

  const marginLabel = areJobLevelRates
    ? i18n.t('project_management.jobs_and_rates.sidepanels.form.min_margin.label')
    : i18n.t('project_management.people_and_rates.form.margin.label')

  return (
    <Card>
      <Card.Content>
        <Box paddingBottom="s20">
          <Box flexDirection="row" gap="s8">
            <Text size="300" color="grey1000" weight="semibold">
              {moment(startsOn).format('Do MMMM, YYYY')}
            </Text>
            {!endsOn && (
              <F0TagStatus
                variant="positive"
                text={i18n.t('project_management.projects.status.active')}
              />
            )}
            {totalCount > 0 && <F0TagRaw text={getPeopleCount(totalCount)} />}
            {rateType === 'custom' && (
              <F0TagRaw
                icon={LinkRemove}
                text={i18n.t('project_management.people_and_rates.sidepanel.tags.custom')}
              />
            )}
            {rateType === 'job' && (
              <F0TagRaw
                icon={Link}
                text={i18n.t('project_management.people_and_rates.sidepanel.tags.job')}
              />
            )}
          </Box>
        </Box>
        <Box flexDirection="row" gap="s8" flexWrap="wrap">
          <Text size="200" color="grey800" ellipsis>
            {costRateLabel}
          </Text>
          <Text size="200" color="grey1000" weight="medium" ellipsis>
            {MoneyWithCurrency({
              amount: chargingCostCents,
              currency: currency,
              optionalDecimals: true,
              locale,
            })}
          </Text>
          <Divider vertical />
          <Text size="200" color="grey800" ellipsis>
            {i18n.t('project_management.jobs_and_rates.sidepanels.form.bill_rate.label')}
          </Text>
          <Text size="200" color="grey1000" weight="medium" ellipsis>
            {MoneyWithCurrency({
              amount: billingRateCents,
              currency: currency,
              optionalDecimals: true,
              locale,
            })}
          </Text>
          <Divider vertical />
          <Text size="200" color="grey800" ellipsis>
            {markupLabel}
          </Text>
          <Text size="200" color="grey1000" weight="medium" ellipsis>
            {`${convertCentsToPercentage(markupPercentageCents)}%`}
          </Text>
          <Divider vertical />
          <Text size="200" color="grey800" ellipsis>
            {marginLabel}
          </Text>
          <Text size="200" color="grey1000" weight="medium" ellipsis>
            {`${(marginPercentageCents / 100).toFixed(2)}%`}
          </Text>
        </Box>
      </Card.Content>
    </Card>
  )
}

export default RateInfoCard
